import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { TokenPayload } from '../../models/auth.model';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  
  constructor() {}

  /**
   * Decode JWT token and return the payload
   * 
   * @param token JWT token to decode
   * @returns Decoded token payload or null if invalid
   */
  decodeToken(token: string): TokenPayload | null {
    try {
      return jwtDecode<TokenPayload>(token);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   * 
   * @param token JWT token to check
   * @returns True if token is expired, false otherwise
   */
  isTokenExpired(token: string): boolean {
    const decoded = this.decodeToken(token);
    if (!decoded) return true;
    
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  }

  /**
   * Get time remaining before token expires (in seconds)
   * 
   * @param token JWT token
   * @returns Seconds until token expires, or 0 if already expired
   */
  getTokenRemainingTime(token: string): number {
    const decoded = this.decodeToken(token);
    if (!decoded) return 0;
    
    const currentTime = Math.floor(Date.now() / 1000);
    const timeRemaining = decoded.exp - currentTime;
    
    return timeRemaining > 0 ? timeRemaining : 0;
  }

  /**
   * Extract user roles from token
   * 
   * @param token JWT token
   * @returns Array of user roles or empty array if invalid
   */
  getUserRoles(token: string): string[] {
    const decoded = this.decodeToken(token);
    return decoded?.roles || [];
  }

  /**
   * Check if token contains specific role
   * 
   * @param token JWT token
   * @param role Role to check for
   * @returns True if token contains role, false otherwise
   */
  hasRole(token: string, role: string): boolean {
    const roles = this.getUserRoles(token);
    return roles.includes(role);
  }
}